import { useState, useEffect, useRef } from 'react';
import './Dashboard.css';
import MapView from './MapView';
import api from './api';

const THRESHOLD = 30;
const MAX_VAL = 60;

const STATUS_LABELS = { 0: '대기', 1: '승인', 2: '운항중', 3: '완료', 4: '취소' };

const capabilities = [
  { id: '01', text: '다중 조건 필터링 조회', sub: '기간 · 버티포트 · 기체 · 슬롯' },
  { id: '02', text: 'ETA 오차 분석 (PASS / FAILED)', sub: null },
  { id: '03', text: '시간대별 성능 분포 차트', sub: null },
  { id: '04', text: '실패 케이스 드릴다운', sub: 'Cause Tag' },
  { id: '05', text: 'CSV 리포트 익스포트', sub: null },
];

// SVG chart constants — PR=50 확보 (우측 y축 라벨 공간)
const CW = 700, CH = 220, PL = 50, PR = 50, PT = 16, PB = 36;
const IW = CW - PL - PR, IH = CH - PT - PB;
const Y_LABELS  = [0, 15, 30, 45, 60];
const X_LABELS  = ['00', '04', '08', '12', '16', '20', '23'];

function yPos(v) { return PT + IH - (v / MAX_VAL) * IH; }

function calcDelay(fp) {
  if (!fp.actualArrTime || !fp.scheduledArrTime) return null;
  return Math.round(Math.abs(new Date(fp.actualArrTime) - new Date(fp.scheduledArrTime)) / 1000);
}

function EtaChart({ hourlyData, flightPlans, vpList }) {
  const [etaTooltip,    setEtaTooltip]    = useState(null);
  const [flightTooltip, setFlightTooltip] = useState(null);

  if (!hourlyData?.length) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: '#4a6a4a', fontFamily: 'monospace', fontSize: 12 }}>
        데이터 없음
      </div>
    );
  }

  const maxFlights = Math.max(1, ...hourlyData.map(d => d.total));
  const fMid    = Math.round(maxFlights / 2);
  const fLabels = [0, fMid, maxFlights];
  function yPosF(v) { return PT + IH - (v / maxFlights) * IH; }

  const barGrpW = IW / hourlyData.length;
  const gap = 3;
  const bw  = barGrpW - gap;
  const etaBw = bw * 0.6;
  const etaOffX = bw * 0.2;

  function findVtp(uid) {
    return vpList?.find(v => String(v.vertiportUid) === String(uid));
  }

  function showFlightTooltip(e, d) {
    const flights = (flightPlans ?? []).filter(fp => {
      if (!fp.scheduledArrTime) return false;
      return parseInt(fp.scheduledArrTime.slice(11, 13), 10) === d.hour;
    });
    setFlightTooltip({ hour: d.hour, total: d.total, flights, mx: e.clientX, my: e.clientY });
  }

  return (
    <div style={{ position: 'relative' }}>
      <svg
        className="eta-chart"
        viewBox={`0 0 ${CW} ${CH}`}
        width="100%"
        aria-label="24시간 ETA 오차 · 편수 분포 차트"
      >
        {/* 좌측 grid + y 라벨 (ETA 오차, 초) */}
        {Y_LABELS.map(v => (
          <g key={v}>
            <line
              x1={PL} y1={yPos(v)} x2={CW - PR} y2={yPos(v)}
              stroke={v === THRESHOLD ? 'none' : '#172217'} strokeWidth="1"
            />
            <text x={PL - 6} y={yPos(v) + 4}
              textAnchor="end" fill="#4a6a4a" fontSize="10" fontFamily="monospace">
              {v}s
            </text>
          </g>
        ))}

        {/* 30s threshold 점선 */}
        <line
          x1={PL} y1={yPos(THRESHOLD)} x2={CW - PR} y2={yPos(THRESHOLD)}
          stroke="#f0b429" strokeWidth="1" strokeDasharray="5 3" opacity="0.65"
        />
        <text x={CW - PR - 2} y={yPos(THRESHOLD) - 4}
          textAnchor="end" fill="#f0b429" fontSize="10" fontFamily="monospace">
          30s
        </text>

        {/* 우측 y축 라인 */}
        <line x1={CW - PR} y1={PT} x2={CW - PR} y2={PT + IH} stroke="#1e3a1e" strokeWidth="1" />

        {/* 우측 y축 라벨 (flights 편수) */}
        {fLabels.map(v => (
          <g key={`fl-${v}`}>
            <line x1={CW - PR} y1={yPosF(v)} x2={CW - PR + 4} y2={yPosF(v)} stroke="#3a5a3a" strokeWidth="1" />
            <text x={CW - PR + 7} y={yPosF(v) + 4}
              textAnchor="start" fill="#3a5a3a" fontSize="9" fontFamily="monospace">
              {v}
            </text>
          </g>
        ))}
        <text x={CW - PR + 7} y={PT - 3}
          textAnchor="start" fill="#3a5a3a" fontSize="8" fontFamily="monospace">편</text>

        {/* flights 편수 바 (회색, ETA 바 뒤) — 호버 이벤트 포함 */}
        {hourlyData.map((d, i) => {
          const bh = (d.total / maxFlights) * IH;
          const x  = PL + i * barGrpW + gap / 2;
          const y  = PT + IH - bh;
          return (
            <rect
              key={`fl-${d.hour}`}
              x={x} y={y} width={bw} height={Math.max(bh, 0)}
              fill="#c8d8c8" opacity="0.12"
              stroke="#4a7a4a" strokeWidth="0.5" strokeOpacity="0.25"
              rx="1"
              style={{ cursor: 'pointer' }}
              onMouseEnter={e => showFlightTooltip(e, d)}
              onMouseMove={e => setFlightTooltip(prev => prev ? { ...prev, mx: e.clientX, my: e.clientY } : null)}
              onMouseLeave={() => setFlightTooltip(null)}
            />
          );
        })}

        {/* ETA 오차 바 (녹/적, flights 바 앞) */}
        {hourlyData.map((d, i) => {
          const val  = d.avgEtaError ?? 0;
          const pass = val <= THRESHOLD;
          const bh   = Math.min(val, MAX_VAL) / MAX_VAL * IH;
          const x    = PL + i * barGrpW + gap / 2 + etaOffX;
          const y    = PT + IH - bh;
          return (
            <rect
              key={d.hour}
              x={x} y={y} width={etaBw} height={Math.max(bh, 0)}
              fill={pass ? '#00d46a' : '#ff4d4d'}
              opacity="0.85" rx="1"
              style={{ cursor: 'default', pointerEvents: 'none' }}
            />
          );
        })}

        {/* ETA 오차 바 위에 투명 오버레이 — 툴팁 트리거 (flights 툴팁 위 우선 표시) */}
        {hourlyData.map((d, i) => {
          const val  = d.avgEtaError ?? 0;
          const pass = val <= THRESHOLD;
          const bh   = Math.min(val, MAX_VAL) / MAX_VAL * IH;
          const x    = PL + i * barGrpW + gap / 2 + etaOffX;
          const y    = PT + IH - bh;
          const label = String(d.hour).padStart(2, '0');
          return (
            <rect
              key={`eta-ov-${d.hour}`}
              x={x} y={y} width={etaBw} height={Math.max(bh, 0)}
              fill="transparent"
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => {
                setFlightTooltip(null);
                setEtaTooltip({ x: x + etaBw / 2, y, label, val, pass, total: d.total });
              }}
              onMouseLeave={() => setEtaTooltip(null)}
            />
          );
        })}

        {/* ETA SVG 툴팁 */}
        {etaTooltip && (() => {
          const { x, y, label, val, pass, total } = etaTooltip;
          const tw = 96, th = 48;
          const tx = Math.min(Math.max(x - tw / 2, PL), CW - PR - tw);
          const ty = Math.max(y - th - 8, PT);
          return (
            <g>
              <rect x={tx} y={ty} width={tw} height={th}
                fill="#0f1a0f" stroke={pass ? '#00d46a' : '#ff4d4d'} strokeWidth="1" rx="2" />
              <text x={tx + tw / 2} y={ty + 14} textAnchor="middle"
                fill="#c8d8c8" fontSize="10" fontFamily="monospace">{label}:00 · {val}s</text>
              <text x={tx + tw / 2} y={ty + 28} textAnchor="middle"
                fill="#7aaa7a" fontSize="9" fontFamily="monospace">{total}편</text>
              <text x={tx + tw / 2} y={ty + 42} textAnchor="middle"
                fill={pass ? '#00d46a' : '#ff4d4d'} fontSize="9" fontFamily="monospace">
                {pass ? '● PASS' : '● FAILED'}
              </text>
            </g>
          );
        })()}

        {/* x 라벨 */}
        {X_LABELS.map(h => {
          const i = hourlyData.findIndex(d => d.hour === parseInt(h, 10));
          if (i < 0) return null;
          const x = PL + i * barGrpW + barGrpW / 2;
          return (
            <text key={h} x={x} y={CH - PB + 14}
              textAnchor="middle" fill="#4a6a4a" fontSize="10" fontFamily="monospace">{h}</text>
          );
        })}

        {/* 범례 */}
        <g transform={`translate(${PL}, ${CH - 10})`}>
          <rect x={0}   y={-7} width={7} height={7} fill="#c8d8c8" opacity="0.3" rx="1" />
          <text x={11}  y={0} fill="#4a6a4a" fontSize="9" fontFamily="monospace">FLIGHTS</text>
          <rect x={64}  y={-7} width={7} height={7} fill="#00d46a" rx="1" />
          <text x={75}  y={0} fill="#4a6a4a" fontSize="9" fontFamily="monospace">PASS</text>
          <rect x={110} y={-7} width={7} height={7} fill="#ff4d4d" rx="1" />
          <text x={121} y={0} fill="#4a6a4a" fontSize="9" fontFamily="monospace">FAILED(&gt;30s)</text>
        </g>
      </svg>

      {/* Flights 바 HTML 팝업 — 비행편 상세 테이블 */}
      {flightTooltip && (() => {
        const { hour, total, flights, mx, my } = flightTooltip;
        const tipW = Math.min(600, window.innerWidth - 20);
        const tx = mx + 14 + tipW > window.innerWidth ? Math.max(10, mx - tipW - 14) : mx + 14;
        const ty = Math.max(10, Math.min(my - 10, window.innerHeight - 120));
        const tipMaxBodyH = Math.max(160, window.innerHeight - ty - 60);
        const label = String(hour).padStart(2, '0');
        return (
          <div className="fbt-wrap" style={{ left: tx, top: ty, width: tipW }}>
            <div className="fbt-header">
              {label}:00 도착 · {total}편
            </div>
            <div className="fbt-body" style={{ maxHeight: tipMaxBodyH }}>
              {flights.length === 0 ? (
                <div className="fbt-empty">해당 시간대 비행편 없음</div>
              ) : (
                <table className="fbt-table">
                  <thead>
                    <tr>
                      <th>비행편</th>
                      <th>출발 버티포트</th>
                      <th>출발시간</th>
                      <th>도착 버티포트</th>
                      <th>도착시간</th>
                      <th>딜레이</th>
                    </tr>
                  </thead>
                  <tbody>
                    {flights.map(fp => {
                      const dep   = findVtp(fp.depVertiportUid);
                      const arr   = findVtp(fp.arrVertiportUid);
                      const delay = calcDelay(fp);
                      const depTime = fp.scheduledDepTime?.slice(11, 16) ?? '-';
                      const arrTime = fp.scheduledArrTime?.slice(11, 16) ?? '-';
                      return (
                        <tr key={fp.flightPlanUid}>
                          <td className="fbt-id">{fp.flightPlanId}</td>
                          <td className="fbt-vtp">{dep?.name ?? `VTP-${fp.depVertiportUid}`}</td>
                          <td className="fbt-time">{depTime}</td>
                          <td className="fbt-vtp">{arr?.name ?? `VTP-${fp.arrVertiportUid}`}</td>
                          <td className="fbt-time">{arrTime}</td>
                          <td className={delay == null ? 'fbt-na' : delay <= 30 ? 'fbt-pass' : 'fbt-fail'}>
                            {delay == null ? '진행중' : `${delay}s`}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default function Dashboard({ session, onLogout, onAdminClick, onHome }) {
  const [view, setView] = useState('dashboard');

  // 기본값: 오늘 00:00 ~ 오늘 23:59
  const todayStr = new Date().toISOString().slice(0, 10);
  const initFrom = `${todayStr}T00:00`;
  const initTo   = `${todayStr}T23:59`;

  // 입력 중인 draft 값 (UI에 표시)
  const [draftDateFrom, setDraftDateFrom] = useState(initFrom);
  const [draftDateTo,   setDraftDateTo]   = useState(initTo);
  const [draftVtp,      setDraftVtp]      = useState(null);
  const [draftUav,      setDraftUav]      = useState(null);

  // 실제 적용된 필터 (적용 버튼 클릭 시에만 변경)
  const [applied, setApplied] = useState({ dateFrom: initFrom, dateTo: initTo, vtp: null, uav: null });

  const [flightPlans, setFlightPlans] = useState([]);
  const [acList,      setAcList]      = useState([]);
  const [vpList,      setVpList]      = useState([]);
  const [stats,       setStats]       = useState(null);
  const [fpLoading,   setFpLoading]   = useState(true);

  // 인터벌에서 최신 적용 필터 참조용
  const appliedRef = useRef(applied);
  useEffect(() => { appliedRef.current = applied; }, [applied]);

  async function fetchData(filter) {
    const startDate = filter.dateFrom.slice(0, 10);
    const endDate   = filter.dateTo.slice(0, 10);
    const params = new URLSearchParams({ start: startDate, end: endDate });
    if (filter.vtp?.vertiportUid) params.set('vertiportUid', filter.vtp.vertiportUid);
    if (filter.uav?.aircraftUid)  params.set('aircraftUid',  filter.uav.aircraftUid);
    const [fpRes, statsRes] = await Promise.all([
      api.get(`/api/flight-plan/list?start=${startDate}&end=${endDate}`),
      api.get(`/api/flight-plan/stats?${params}`),
    ]);
    setFlightPlans(fpRes.data);
    setStats(statsRes.data);
  }

  // 초기 로드: 드롭다운 목록 + 기본 필터로 데이터 조회
  useEffect(() => {
    async function init() {
      try {
        const [acRes, vpRes] = await Promise.all([
          api.get('/api/aircraft/list'),
          api.get('/api/vertiport/list'),
        ]);
        setAcList(acRes.data);
        setVpList(vpRes.data);
        await fetchData(appliedRef.current);
      } catch {
        // 로그인 인터셉터가 401 처리
      } finally {
        setFpLoading(false);
      }
    }
    init();

    const timer = setInterval(async () => {
      try { await fetchData(appliedRef.current); } catch {}
    }, 30000);
    return () => clearInterval(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 적용 버튼 핸들러
  async function handleApply() {
    const next = { dateFrom: draftDateFrom, dateTo: draftDateTo, vtp: draftVtp, uav: draftUav };
    setApplied(next);
    setFpLoading(true);
    try { await fetchData(next); } catch {} finally { setFpLoading(false); }
  }

  function findAc(uid) { return acList.find(a => String(a.aircraftUid) === String(uid)); }
  function findVp(uid) { return vpList.find(v => String(v.vertiportUid) === String(uid)); }

  // 클라이언트 필터: 시간 범위 + vtp/uav
  const filteredPlans = flightPlans.filter(fp => {
    // 시간 범위 필터 (서버는 날짜만 받으므로 시간은 클라이언트에서 처리)
    const ref = fp.scheduledDepTime ?? fp.scheduledArrTime;
    if (ref) {
      const t = ref.slice(0, 16); // YYYY-MM-DDTHH:mm
      if (t < applied.dateFrom || t > applied.dateTo) return false;
    }
    if (applied.vtp) {
      const uid = String(applied.vtp.vertiportUid);
      if (String(fp.depVertiportUid) !== uid && String(fp.arrVertiportUid) !== uid) return false;
    }
    if (applied.uav && String(fp.aircraftUid) !== String(applied.uav.aircraftUid)) return false;
    return true;
  });

  // 운항중(status=2) 제외 후 pass/fail 클라이언트 집계
  const evaluatedPlans = filteredPlans.filter(fp => fp.status !== 2 && calcDelay(fp) != null);
  const clientFailed   = evaluatedPlans.filter(fp => calcDelay(fp) > 30).length;
  const clientPassed   = evaluatedPlans.length - clientFailed;
  const clientPassRate = evaluatedPlans.length > 0 ? Math.round(clientPassed / evaluatedPlans.length * 100) : 0;

  function handleExportCSV() {
    const headers = ['비행계획ID', '기체', '출발버티포트', '도착버티포트', '계획출발', '실제출발', '계획도착', '실제도착', '딜레이(초)', '결과', '상태'];
    const rows = filteredPlans.map(fp => {
      const ac  = findAc(fp.aircraftUid);
      const dep = findVp(fp.depVertiportUid);
      const arr = findVp(fp.arrVertiportUid);
      const isFlying = fp.status === 2;
      const delay = calcDelay(fp);
      const passed = !isFlying && delay != null ? delay <= 30 : null;
      return [
        fp.flightPlanId,
        ac?.aircraftId ?? `#${fp.aircraftUid}`,
        dep?.name ?? `VTP-${fp.depVertiportUid}`,
        arr?.name ?? `VTP-${fp.arrVertiportUid}`,
        fp.scheduledDepTime ? fp.scheduledDepTime.replace('T', ' ').slice(0, 16) : '',
        fp.actualDepTime    ? fp.actualDepTime.replace('T', ' ').slice(0, 16)    : '',
        fp.scheduledArrTime ? fp.scheduledArrTime.replace('T', ' ').slice(0, 16) : '',
        isFlying ? '' : (fp.actualArrTime ? fp.actualArrTime.replace('T', ' ').slice(0, 16) : ''),
        isFlying ? '' : (delay != null ? String(delay) : ''),
        passed === null ? '' : (passed ? 'PASS' : 'FAIL'),
        STATUS_LABELS[fp.status] ?? String(fp.status),
      ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',');
    });
    const csv = '﻿' + [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `flight-list-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const passRate = clientPassRate;

  return (
    <div className="db">
      {/* ── Header ── */}
      <header className="db-header">
        <div className="db-header-l">
          <img src="/logo-typo.png" alt="MIN-SOFT" className="db-logo" onClick={onHome} />
          <span className="db-divider" />
          <span className="db-title">성능 분석 대시보드</span>
          <span className="db-sub">예측 ETA vs 실제 도착 · PASS / FAILED 기준 관제 로직 품질 평가</span>
        </div>
        <div className="db-header-r">
          <span className="db-live-dot" />
          <span className="db-live">LIVE · DAEGU-GB METRO</span>
          <button
            className={`btn-map${view === 'map' ? ' active' : ''}`}
            onClick={() => setView(v => v === 'map' ? 'dashboard' : 'map')}
          >
            {view === 'map' ? '상황표' : '지도보기'}
          </button>
          <span className="db-user">{session?.userName} [{session?.userId}]</span>
          {session?.authLevel === 2 && (
            <button className="btn-admin" onClick={onAdminClick}>관리자 모드</button>
          )}
          <button className="btn-logout" onClick={onLogout}>로그아웃</button>
        </div>
      </header>

      {view === 'map' && <MapView session={session} />}
      <main className="db-main" style={{ display: view === 'map' ? 'none' : 'flex' }}>

        {/* ── Filter bar ── */}
        <section className="db-filter">
          <div className="fg">
            <label>기간</label>
            <input type="datetime-local" value={draftDateFrom} onChange={e => setDraftDateFrom(e.target.value)} />
            <span className="fg-sep">~</span>
            <input type="datetime-local" value={draftDateTo}   onChange={e => setDraftDateTo(e.target.value)} />
          </div>
          <div className="fg">
            <label>버티포트</label>
            <select
              value={draftVtp?.vertiportUid ?? ''}
              onChange={e => {
                const uid = e.target.value;
                setDraftVtp(uid ? (vpList.find(v => String(v.vertiportUid) === uid) ?? null) : null);
              }}
            >
              <option value="">전체</option>
              {vpList.map(v => (
                <option key={v.vertiportUid} value={v.vertiportUid}>{v.name}</option>
              ))}
            </select>
          </div>
          <div className="fg">
            <label>기체</label>
            <select
              value={draftUav?.aircraftUid ?? ''}
              onChange={e => {
                const uid = e.target.value;
                setDraftUav(uid ? (acList.find(a => String(a.aircraftUid) === uid) ?? null) : null);
              }}
            >
              <option value="">전체</option>
              {acList.map(a => (
                <option key={a.aircraftUid} value={a.aircraftUid}>{a.aircraftId}</option>
              ))}
            </select>
          </div>
          <div className="fg">
            <button className="btn-apply" onClick={handleApply}>적용</button>
          </div>
          <div className="fg-export">
            <button className="btn-exp" onClick={handleExportCSV}>CSV 내보내기</button>
          </div>
        </section>

        {/* ── KPI cards ── */}
        <section className="kpi-row">
          <div className="kpi-card">
            <div className="kpi-lbl">TOTAL FLIGHTS</div>
            <div className="kpi-val">{stats ? stats.total.toLocaleString() : '-'}</div>
            <div className="kpi-desc">24시간 시뮬레이션 누적</div>
          </div>
          <div className="kpi-card kpi-green-top">
            <div className="kpi-lbl">PASS RATE</div>
            <div className="kpi-val c-green">{fpLoading ? '-' : `${clientPassRate}%`}</div>
            <div className="kpi-desc">ETA 오차 ≤ 30s · 목표 ≥ 90%</div>
            <div className="kpi-bar-wrap">
              <div className="kpi-bar-fill" style={{ width: `${passRate}%` }} />
            </div>
          </div>
          <div className="kpi-card kpi-yellow-top">
            <div className="kpi-lbl">AVG ETA ERROR</div>
            <div className="kpi-val c-yellow">{stats ? `${stats.avgEtaError}s` : '-'}</div>
            <div className="kpi-desc">평균 절대 오차 (MAE)</div>
          </div>
          <div className="kpi-card kpi-red-top">
            <div className="kpi-lbl">FAILED</div>
            <div className="kpi-val c-red">{fpLoading ? '-' : clientFailed}</div>
            <div className="kpi-desc">오차 &gt; 30s · 회귀 분석 대상</div>
          </div>
        </section>

        {/* ── Chart + Capabilities ── */}
        <section className="db-mid">
          <div className="panel chart-panel">
            <div className="panel-hd">
              <span className="panel-title">ETA ERROR DISTRIBUTION · 24H</span>
              <span className="panel-sub">HOURLY ABS ETA ERROR (sec) · FLIGHTS COUNT</span>
            </div>
            <EtaChart
              hourlyData={stats?.hourly ?? []}
              flightPlans={filteredPlans}
              vpList={vpList}
            />
          </div>

          <div className="panel cap-panel">
            <div className="panel-hd">
              <span className="panel-title">DASHBOARD CAPABILITIES</span>
            </div>
            <ul className="cap-list">
              {capabilities.map(c => (
                <li key={c.id} className="cap-item">
                  <span className="cap-id">[{c.id}]</span>
                  <div>
                    <div className="cap-text">{c.text}</div>
                    {c.sub && <div className="cap-sub">{c.sub}</div>}
                  </div>
                </li>
              ))}
            </ul>
            <div className="cap-note">
              배정 알고리즘 교체 시 동일 지표로 직접 비교 가능 →<br />
              AI 모델 도입 효과를 정량 검증
            </div>
          </div>
        </section>

        {/* ── Flight List ── */}
        <section className="panel failed-panel">
          <div className="panel-hd">
            <span className="panel-title">FLIGHT LIST · REAL-TIME</span>
            {!fpLoading && (
              <span className="fp-count-badge">{filteredPlans.length}건</span>
            )}
          </div>
          {fpLoading ? (
            <div className="fp-empty">불러오는 중...</div>
          ) : filteredPlans.length === 0 ? (
            <div className="fp-empty">등록된 비행 계획이 없습니다.</div>
          ) : (
            <div className="tbl-wrap">
              <table className="failed-tbl">
                <thead>
                  <tr>
                    <th>비행계획 ID</th><th>기체</th><th>출발</th><th>도착</th>
                    <th>계획 출발</th><th>실제 출발</th><th>계획 도착</th><th>실제 도착</th><th>딜레이</th><th>결과</th><th>상태</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPlans.map(fp => {
                    const ac  = findAc(fp.aircraftUid);
                    const dep = findVp(fp.depVertiportUid);
                    const arr = findVp(fp.arrVertiportUid);
                    const isFlying = fp.status === 2;
                    const delay = calcDelay(fp);
                    const passed = !isFlying && delay != null ? delay <= 30 : null;
                    return (
                      <tr key={fp.flightPlanUid} className="failed-row">
                        <td className="mono">{fp.flightPlanId}</td>
                        <td className="mono uav-id">{ac?.aircraftId ?? `#${fp.aircraftUid}`}</td>
                        <td>{dep?.name ?? `VTP-${fp.depVertiportUid}`}</td>
                        <td>{arr?.name ?? `VTP-${fp.arrVertiportUid}`}</td>
                        <td className="mono">{fp.scheduledDepTime ? fp.scheduledDepTime.replace('T', ' ').slice(0, 16) : '-'}</td>
                        <td className="mono">{fp.actualDepTime    ? fp.actualDepTime.replace('T', ' ').slice(0, 16)    : '-'}</td>
                        <td className="mono">{fp.scheduledArrTime ? fp.scheduledArrTime.replace('T', ' ').slice(0, 16) : '-'}</td>
                        <td className="mono">{isFlying ? '' : (fp.actualArrTime ? fp.actualArrTime.replace('T', ' ').slice(0, 16) : '-')}</td>
                        <td className={delay == null || isFlying ? 'fp-na' : delay <= 30 ? 'fp-pass' : 'fp-fail'}>
                          {isFlying ? '' : (delay != null ? `${delay}s` : '-')}
                        </td>
                        <td>
                          {passed === null
                            ? <span className="fp-na">-</span>
                            : passed
                              ? <span className="fp-result-pass">PASS</span>
                              : <span className="fp-result-fail">FAIL</span>
                          }
                        </td>
                        <td>
                          <span className={`fp-status-badge fp-s${fp.status}`}>
                            {STATUS_LABELS[fp.status] ?? fp.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>

      <footer className="db-footer">
        <span>MINSOFT · UAM CONTROL PROTOTYPE · UAM-CTL-2026</span>
        <span>CONFIDENTIAL · INTERNAL USE ONLY</span>
      </footer>
    </div>
  );
}
